import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { programDeliveryFilters } from "../filterConfigurations";
import SimpleToolbar from "../../common/SimpleToolbar";
import { formatDate } from "../../../utils/formatDate";

const ProgramDelivery = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Program Delivery"]?.edit;
  const {
    data: programDeliveryData,
    loading,
    error,
  } = useFirestore("ProgramDelivery");

  // Fetch Sites data for the Sites dropdown
  const {
    data: sitesData,
    loading: sitesLoading,
    error: sitesError,
  } = useFirestore("Sites", "SiteName", "asc");

  // Create dynamic filter configuration with Sites options
  const dynamicFilters = useMemo(() => {
    const filters = [...programDeliveryFilters];

    // Find and update the Sites filter with actual data
    const sitesFilterIndex = filters.findIndex(
      (f) => f.name === "SiteOfDelivery",
    );
    if (sitesFilterIndex !== -1 && sitesData) {
      // Create options that try to match different possible formats
      const siteOptions = sitesData.map((site) => {
        // Based on the Add form, the value is stored as SiteID, but let's handle multiple formats
        const possibleValues = [
          site.SiteID, // Primary value (what's stored)
          site.SiteName, // Full site name
          site.SiteNameShort, // Short name
          `${site.SiteID} - ${site.SiteName}`, // ID - Name format
          `${site.SiteName} (${site.SiteID})`, // Name (ID) format
          `${site.SiteID}`, // String version of ID
          site.id, // Firestore document ID fallback
        ].filter(Boolean);

        return {
          value: site.SiteName || "Unknown", // Display the site name as the value
          label: site.SiteName || "Unknown", // Display the site name
          siteData: site, // Store the full site data
          searchValues: possibleValues, // Store all possible values for matching
        };
      });

      filters[sitesFilterIndex] = {
        ...filters[sitesFilterIndex],
        options: siteOptions,
        // Add custom filter function to handle multiple match possibilities
        customFilter: (itemValue, filterValue, options) => {
          if (!itemValue || !filterValue) return true;

          // Find the selected option
          const selectedOption = options.find(
            (opt) => opt.value === filterValue,
          );
          if (!selectedOption) {
            // Fallback to exact match if no option found
            return (
              String(itemValue).toLowerCase() ===
              String(filterValue).toLowerCase()
            );
          }

          // Convert values to strings and normalize
          const itemStr = String(itemValue).toLowerCase().trim();

          // Check if the item value matches any of the possible values for this site
          const matches = selectedOption.searchValues.some((searchValue) => {
            if (!searchValue) return false;

            const searchStr = String(searchValue).toLowerCase().trim();

            // Exact match
            if (itemStr === searchStr) return true;

            // Contains match (both ways)
            if (itemStr.includes(searchStr) || searchStr.includes(itemStr))
              return true;

            // For numeric IDs, also try comparing as numbers
            if (!isNaN(itemValue) && !isNaN(searchValue)) {
              if (Number(itemValue) === Number(searchValue)) return true;
            }

            return false;
          });

          return matches;
        },
      };
    }

    return filters;
  }, [sitesData]);

  const [selectedProgramDelivery, setSelectedProgramDelivery] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([
    { field: "SiteOfDelivery", sort: "asc" },
  ]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "Program Delivery";

  // Use custom filter hook
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(programDeliveryData || [], dynamicFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const getBooleanDisplayValue = (value) => {
    if (value === 0 || value === "0") return "No";
    if (value === -1 || value === "-1") return "Yes";
    if (value === null || typeof value === "undefined") return "";
    return String(value);
  };

  const columns = useMemo(
    () => [
      {
        field: "CCcode",
        headerName: "CC Code",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "PathwayID",
        headerName: "Pathway ID",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "ProgramDeliveryID",
        headerName: "Program Delivery ID",
        flex: 2,
        minWidth: 200,
      },
      {
        field: "SiteOfDelivery",
        headerName: "Site of Delivery",
        flex: 2,
        minWidth: 200,
      },
      {
        field: "SOD_NameForApp",
        headerName: "SOD Name for App",
        flex: 2,
        minWidth: 200,
      },
      {
        field: "BegDate",
        headerName: "Begin Date",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: "EndDate",
        headerName: "End Date",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => formatDate(params.value),
      },
    ],
    [],
  );

  const templateFields = Array.from(
    new Set([
      ...columns.map((col) => col.field),
      "HighSchool",
      "CoordinatorID",
      "NonTPconsortium",
      "SiteID",
      "SODcode",
      "SC_SoD_Inst_Id_Nb",
      "Notes",
    ]),
  );

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedProgramDelivery(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedProgramDelivery(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    // Data will refresh automatically due to onSnapshot in useFirestore
    // Snackbar for add will be handled in Add.js
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box
        sx={{
          marginTop: "100px",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 100px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "lg",
            margin: "0 auto",
            width: "100%",
            padding: "0 16px 16px 16px",
            boxSizing: "border-box",
          }}
        >
          <TechPrepPortalMgtBreadCrumbs pageName={pageName} />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          marginTop: "100px",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 100px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "lg",
            margin: "0 auto",
            width: "100%",
            padding: "0 16px 16px 16px",
            boxSizing: "border-box",
          }}
        >
          <TechPrepPortalMgtBreadCrumbs pageName={pageName} />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Alert severity="error">
            Error loading program delivery data: {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={dynamicFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={{}}
          title="Program Delivery Filters"
          resultCount={filteredData.length}
          totalCount={programDeliveryData?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="ProgramDelivery"
              exportData={programDeliveryData}
              onActionComplete={handleBulkActionComplete}
              booleanFields={["NonTPconsortium"]}
              templateFields={templateFields}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="ProgramDelivery" />
          )}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={filteredData || []}
            columns={columns}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={[25, 50, 100]}
            onRowClick={handleRowClick}
            slots={{
              toolbar: SimpleToolbar,
            }}
            sx={{
              cursor: canEdit ? "pointer" : "default",
              "& .MuiDataGrid-row:hover": {
                backgroundColor: canEdit ? "action.hover" : "transparent",
              },
            }}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
          />
        </Box>
      </div>

      {/* Edit Dialog */}
      {selectedProgramDelivery && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          programDeliveryDetail={selectedProgramDelivery}
          onRecordUpdated={handleRecordUpdated}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProgramDelivery;
